/**
 * @file app/paymentModules/npipe/npipeTransport.web.js
 *
 * Web / Tauri (desktop) NPIPE transport. Uses the same Tauri HTTP plugin and
 * `danger.acceptInvalidCerts` mechanism as `app/tauri/payment.js` (the terminal
 * serves a self-signed certificate) but posts an already built, signed body so
 * every NPIPE action can be sent. Same exports as `npipeTransport.js`.
 */

import { fetch as tauriFetch } from '@tauri-apps/plugin-http';
import { NpipeTransportError, signBody } from './npipeHttp';

export { NpipeTransportError, signBody };

const DEFAULT_TIMEOUT_MS = 60000;

/**
 * Send a signed NPIPE request and resolve with the raw HTTP result.
 * @param {{ host:string, port:number|string, secret:string, rawBody:string, timeoutMs?:number }} params request
 * @returns {Promise<{ status:number, bodyText:string }>} HTTP status and body
 * @throws {NpipeTransportError} TIMEOUT | UNREACHABLE | TLS | BAD_RESPONSE
 */
export async function postSigned({ host, port, secret, rawBody, timeoutMs = DEFAULT_TIMEOUT_MS }) {
    const limit = Number(timeoutMs) > 0 ? Number(timeoutMs) : DEFAULT_TIMEOUT_MS;
    let timer = null;
    const timeout = new Promise((_, reject) => {
        timer = setTimeout(
            () => reject(new NpipeTransportError('TIMEOUT', `No response from ${host}:${port} within ${limit} ms`)),
            limit
        );
    });

    const exchange = (async () => {
        let response;
        try {
            response = await tauriFetch(`https://${host}:${port}/perform_action`, {
                method: 'POST',
                headers: {
                    'X-Request-Hash': signBody(rawBody, secret),
                    'Content-Type': 'application/json; charset=UTF-8',
                },
                body: rawBody,
                connectTimeout: limit,
                danger: { acceptInvalidCerts: true, acceptInvalidHostnames: true },
            });
        } catch (e) {
            const msg = String((e && e.message) || e || '');
            throw new NpipeTransportError(/ssl|tls|handshake|certificate/i.test(msg) ? 'TLS' : 'UNREACHABLE', msg);
        }
        try {
            return { status: response.status, bodyText: await response.text() };
        } catch (e) {
            throw new NpipeTransportError('BAD_RESPONSE', String((e && e.message) || e));
        }
    })();

    try {
        return await Promise.race([exchange, timeout]);
    } finally {
        clearTimeout(timer);
        exchange.catch(() => {});
    }
}

export default postSigned;
